import { useEffect, useRef } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'

import { fetchLabResultsByMission } from '@services/data.service'

import {
  getLabResultAnalysisType,
  getLabResultHoverKey,
  getVisibleLabResultAnalysis,
  setClickedResultKey,
  setHoveredClusterResultKeys,
  setHoveredItem,
} from './lab-results.slice'

import './lab-results.styl'

const EMPTY_RESULTS = []

function getLabResultLocationKey(coordinates) {
  return coordinates.map(coordinate => Number(coordinate).toFixed(5)).join(',')
}

function getMapFeatureHoverKeys(feature) {
  const rawHoverKeys = feature?.properties?.hoverKeys
  if (Array.isArray(rawHoverKeys)) return rawHoverKeys.filter(Boolean)

  if (typeof rawHoverKeys === 'string') {
    try {
      const parsedHoverKeys = JSON.parse(rawHoverKeys)
      if (Array.isArray(parsedHoverKeys)) return parsedHoverKeys.filter(Boolean)
    } catch {
      return [rawHoverKeys]
    }
  }

  const hoverKey = feature?.properties?.hoverKey
  return hoverKey ? [hoverKey] : []
}

function getResultKeysAtLocation(results, coordinates) {
  if (!Array.isArray(coordinates)) return []

  return results
    .filter(result => getLabResultLocationKey(result.geometry?.coordinates || []) === getLabResultLocationKey(coordinates))
    .map(getLabResultHoverKey)
}

function getLabResultMapFeatures(results, activeAnalysisTypeIds, activeResultTypes) {
  const featuresByLocation = new Map()

  results.forEach((result) => {
    const properties = result.properties || {}
    const analysis = Array.isArray(properties.analysis) ? properties.analysis : []
    const visibleAnalysis = getVisibleLabResultAnalysis(
      analysis,
      activeAnalysisTypeIds,
      activeResultTypes,
    )
    const coordinates = result.geometry?.coordinates
    if (visibleAnalysis.length === 0 || !Array.isArray(coordinates)) return

    const resultCounts = visibleAnalysis.reduce((counts, item) => {
      const resultType = getLabResultAnalysisType(item)
      if (resultType === 'positive' || resultType === 'negative') {
        counts[resultType] += 1
      }
      return counts
    }, { positive: 0, negative: 0 })

    const totalCount = resultCounts.positive + resultCounts.negative
    if (totalCount === 0) return

    const key = getLabResultLocationKey(coordinates)
    const existingFeature = featuresByLocation.get(key)
    if (existingFeature) {
      const featureProperties = existingFeature.properties
      featureProperties.positiveCount += resultCounts.positive
      featureProperties.negativeCount += resultCounts.negative
      const resultHoverKey = getLabResultHoverKey(result)
      if (!featureProperties.hoverKeys.includes(resultHoverKey)) {
        featureProperties.hoverKeys.push(resultHoverKey)
      }
      featureProperties.pieBucket = Math.round(
        (featureProperties.positiveCount / (featureProperties.positiveCount + featureProperties.negativeCount)) * 20,
      )
      featureProperties.isMixed = featureProperties.positiveCount > 0
        && featureProperties.negativeCount > 0
      return
    }

    featuresByLocation.set(key, {
      type: 'Feature',
      geometry: result.geometry,
      properties: {
        hoverKey: getLabResultHoverKey(result),
        hoverKeys: [getLabResultHoverKey(result)],
        positiveCount: resultCounts.positive,
        negativeCount: resultCounts.negative,
        pieBucket: Math.round((resultCounts.positive / totalCount) * 20),
        isMixed: resultCounts.positive > 0 && resultCounts.negative > 0,
      },
    })
  })

  return [...featuresByLocation.values()]
}

function LabResults() {
  const dispatch = useDispatch()
  const lastHoveredClusterIdRef = useRef(null)
  const { map } = useOutletContext()
  const { activeProject } = useSelector(state => state.missionSetup)
  const {
    selectionRange,
    sourcesActiveNames,
    labResultActiveAnalysisTypeIds,
    labResultActiveResultTypes,
    hoveredItem,
  } = useSelector(state => state.labResults)

  const { data: results = EMPTY_RESULTS } = useQuery({
    queryKey: [
      'labResults', activeProject?.id, selectionRange,
      sourcesActiveNames, labResultActiveAnalysisTypeIds,
    ],
    queryFn: () => fetchLabResultsByMission(
      activeProject.id,
      sourcesActiveNames,
      undefined,
      selectionRange.startDate,
      selectionRange.endDate,
      undefined,
      labResultActiveAnalysisTypeIds,
    ),
    enabled: !!activeProject?.id,
  })

  useEffect(() => {
    if (!map) return

    const source = map.getSource('lab-results')
    if (!source) return

    source.setData({
      type: 'FeatureCollection',
      features: getLabResultMapFeatures(
        results,
        labResultActiveAnalysisTypeIds,
        labResultActiveResultTypes,
      ),
    })

    return () => {
      const currentSource = map.getSource('lab-results')
      currentSource?.setData({
        type: 'FeatureCollection',
        features: [],
      })
    }
  }, [labResultActiveAnalysisTypeIds, labResultActiveResultTypes, map, results])

  useEffect(() => {
    if (!map) return

    const source = map.getSource('hovered-lab-results')
    if (!source) return

    const hoveredResult = results.find(result => getLabResultHoverKey(result) === getLabResultHoverKey(hoveredItem))
    if (!hoveredResult) {
      source.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    const properties = hoveredResult.properties || {}
    const analysis = Array.isArray(properties.analysis) ? properties.analysis : []
    const visibleAnalysis = getVisibleLabResultAnalysis(
      analysis,
      labResultActiveAnalysisTypeIds,
      labResultActiveResultTypes,
    )
    const hasPositiveResult = visibleAnalysis.some(
      item => getLabResultAnalysisType(item) === 'positive',
    )

    source.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: hoveredResult.geometry,
        properties: {
          color: hasPositiveResult ? '#FF0000' : '#16B232',
          analysisCount: visibleAnalysis.length,
        },
      }],
    })

    return () => source.setData({ type: 'FeatureCollection', features: [] })
  }, [hoveredItem, labResultActiveAnalysisTypeIds, labResultActiveResultTypes, map, results])

  useEffect(() => {
    if (!map) return

    const layerIds = ['lab-results-layer', 'lab-results-clusters-layer']
    const canvas = map.getCanvas()
    const handleMouseMove = (event) => {
      const hoveredFeature = event?.features?.[0]
      if (!hoveredFeature) return

      canvas.classList.add('pointer-cursor')
      const featureProperties = hoveredFeature.properties || {}
      if (featureProperties.point_count) {
        const clusterId = featureProperties.cluster_id
        if (clusterId === lastHoveredClusterIdRef.current) return

        lastHoveredClusterIdRef.current = clusterId
        dispatch(setHoveredItem(null))
        dispatch(setHoveredClusterResultKeys([]))

        const source = map.getSource('lab-results')
        source?.getClusterLeaves(clusterId, Infinity, 0)
          .then((leaves) => {
            if (lastHoveredClusterIdRef.current !== clusterId) return

            const resultKeys = [...new Set(leaves.flatMap(getMapFeatureHoverKeys))]
            dispatch(setHoveredClusterResultKeys(resultKeys))
          })
          .catch(() => {})
        return
      }

      lastHoveredClusterIdRef.current = null
      const resultKeys = getMapFeatureHoverKeys(hoveredFeature)
      const locationResultKeys = getResultKeysAtLocation(results, hoveredFeature.geometry?.coordinates)
      const resolvedResultKeys = [...new Set([...resultKeys, ...locationResultKeys])]
      const hoveredResult = results.find(result => getLabResultHoverKey(result) === resolvedResultKeys[0])
      if (!hoveredResult) return

      dispatch(setHoveredClusterResultKeys(resolvedResultKeys))
      dispatch(setHoveredItem(hoveredResult))
    }
    const handleMouseLeave = () => {
      canvas.classList.remove('pointer-cursor')
      lastHoveredClusterIdRef.current = null
      dispatch(setHoveredClusterResultKeys([]))
      dispatch(setHoveredItem(null))
    }
    const handleClick = (event) => {
      const clickedFeature = event?.features?.[0]
      if (!clickedFeature || clickedFeature.properties?.point_count) return

      const featureKeys = getMapFeatureHoverKeys(clickedFeature)
      const locationKeys = getResultKeysAtLocation(results, clickedFeature.geometry?.coordinates)
      const resultKeys = [...new Set([...featureKeys, ...locationKeys])]
      if (resultKeys[0]) dispatch(setClickedResultKey(resultKeys[0]))
    }
    let handlersAttached = false
    const attachHandlers = () => {
      if (handlersAttached || !layerIds.every(layerId => map.getLayer(layerId))) return
      layerIds.forEach((layerId) => {
        map.on('mousemove', layerId, handleMouseMove)
        map.on('mouseleave', layerId, handleMouseLeave)
      })
      map.on('click', 'lab-results-layer', handleClick)
      handlersAttached = true
    }

    attachHandlers()
    map.on('idle', attachHandlers)
    map.on('styledata', attachHandlers)

    return () => {
      map.off('idle', attachHandlers)
      map.off('styledata', attachHandlers)
      if (handlersAttached) {
        layerIds.forEach((layerId) => {
          map.off('mousemove', layerId, handleMouseMove)
          map.off('mouseleave', layerId, handleMouseLeave)
        })
        map.off('click', 'lab-results-layer', handleClick)
      }
      canvas.classList.remove('pointer-cursor')
      lastHoveredClusterIdRef.current = null
      dispatch(setHoveredClusterResultKeys([]))
      dispatch(setHoveredItem(null))
      dispatch(setClickedResultKey(null))
    }
  }, [dispatch, map, results])

  return (
    <div className="lab-results">
      <Outlet context={{ map }} />
    </div>
  )
}

export default LabResults
